// Central config for Planet HypeTunes world client.

// The shared HypeTunes API listens on PORT 3001 in dev (see API .env PORT=3001);
// port 3000 is the web front-end, not the API.
//
// Phone/LAN dev fix: when the page is served from a private LAN IP (a phone
// hitting the dev machine, e.g. 10.0.0.244:5174) but the API base points at
// localhost, that localhost is the PHONE's — so every proxied asset + API call
// 404s. Rewrite to the same LAN host:3001. Explicit non-localhost VITE_API_BASE
// (prod: api.hypetunes.com) is always respected.
const LAN_RE = /^(10\.|192\.168\.|172\.(1[6-9]|2\d|3[01])\.)/;
function resolveBase(envVal) {
  const loc = typeof window !== 'undefined' ? window.location.hostname : '';
  const isHttps = typeof window !== 'undefined' && window.location.protocol === 'https:';
  // HTTPS dev (mic-secure-context, see vite.config.js): call the API SAME-ORIGIN
  // ('') so Vite's proxy forwards it — a direct http://…:3001 fetch from an
  // https page is mixed-content-blocked. Prod (explicit https api host) is left
  // untouched: its envVal isn't localhost/private, so it passes straight through.
  if (isHttps && (!envVal || /localhost|127\.0\.0\.1|:\/\/(10\.|192\.168\.|172\.)/.test(envVal))) {
    return '';
  }
  const onLan = LAN_RE.test(loc);
  if (onLan && (!envVal || /localhost|127\.0\.0\.1/.test(envVal))) {
    return `${window.location.protocol}//${loc}:3001`;
  }
  return envVal || 'http://localhost:3001';
}
// Phones have a far tighter GPU/memory ceiling than desktop — used to gate the
// heaviest loads (Gaussian-splat rooms, retina dpr, shadows/MSAA) so iOS Safari
// doesn't OOM-crash ("a problem repeatedly occurred"). Single source of truth.
export const IS_MOBILE = typeof navigator !== 'undefined'
  && (/iPhone|iPad|iPod|Android/i.test(navigator.userAgent)
    || (typeof window !== 'undefined' && 'ontouchstart' in window && Math.min(window.innerWidth, window.innerHeight) < 820));

// ── PERF SWITCHES — the 2026-08-02 A/B, VERDICT IN ──────────────────────────
// Khari ran the live test: video alone was FINE; the heavy traffic cars were
// the lag (Silverado 24 draw calls, Traverse 31 — per car, every frame). So:
//
//   VIDEO — back ON by default. ?video=0 kills all ambient video (every
//     surface falls to its still) if it ever needs re-testing.
//   VEHICLES — ON by default, but the fleet is REBUILT: traffic is now
//     Elise-only (2 draws) in five painted variants (cars.json), so the
//     street keeps its life at ~1/10th the old cost. ?cars=0 empties the
//     roads again for comparison.
//
// Live user-initiated streams (Anam calls, meets, portal travel) were never
// gated — features, not ambience.
export const VIDEO_OFF = typeof window !== 'undefined'
  && new URLSearchParams(window.location.search).get('video') === '0';

export const VEHICLES_OFF = typeof window !== 'undefined'
  && new URLSearchParams(window.location.search).get('cars') === '0';

// iOS specifically (incl. iPadOS reporting as Mac) — pinch-to-zoom on the WebGL
// canvas (OrbitControls dolly) crashes Safari, so camera zoom is disabled there.
export const IS_IOS = typeof navigator !== 'undefined'
  && (/iPad|iPhone|iPod/.test(navigator.userAgent)
    || (navigator.platform === 'MacIntel' && navigator.maxTouchPoints > 1));

// THE HELD PHONE IS OFF. It renders as a CSS3D panel floating at a viewmodel
// offset from the camera, and the styling for that context was never finished —
// it reads as a bug rather than as a phone in someone's hand. Parked rather than
// deleted: the mechanism (PhoneInHand) is sound, only the presentation is unfinished.
//
// ONE flag read by BOTH sides, deliberately. Phone.jsx decides hud-vs-hand and
// PhoneInHand decides whether to draw, off the same condition — turning it off
// in only one of them leaves first person with no phone at all rather than a HUD
// one. ?phonehand=1 brings it back to work on.
export const PHONE_IN_HAND = typeof window !== 'undefined'
  && new URLSearchParams(window.location.search).get('phonehand') === '1';

// Cache-buster for static assets in public/ (GLB models, etc.). Vite hashes JS/CSS
// but copies public/ verbatim, so those URLs are stable forever and browsers/CDNs
// serve stale copies after a re-export. BUMP THIS whenever a public asset changes
// (e.g. re-encoding the mobile GLBs) so every device re-fetches. Use via assetV().
// 20260727a — cars re-encoded to KTX2 at 512, desktop city + skyline KTX2 builds,
// both mesh venues rebuilt (grocery re-joined after an instancing build scattered
// it, and its unmaterialed geometry given a neutral grey). Every one of those
// reused its existing filename, so without this bump the browser keeps serving
// the copy it already has and the rebuild looks like it did nothing.
// 20260727b — the corner store rebuilt from its ORIGINAL source (the previous
// build had been joined to 27 meshes and had lost the structure both of these
// passes need): floating stock clipped (33.6 -> 10.2 tall), duplicate products
// thinned, and a new exterior shell extracted alongside it.
// Both reuse existing filenames, so without this bump the browser keeps serving
// the copies it already has and the rebuild looks like it did nothing.
// 20260727c — corner store again, fixing two things the first pass got wrong.
// Thinning was removing SHELVING along with the goods on it, so products were
// left floating: it now protects anything over 1.5 units, which is where this
// model has a clean empty band between fixtures and stock. And the shell was
// carrying the model's two baked 543-intensity lamps into the OPEN CITY, which
// is why the building did not respond to the day/night rig — stripped at source
// now, with a runtime guard in VenueShells.
// 20260727d — shell rebuilt with a SIZE test alongside the position one. A
// margin tight enough to reject a shelf leaning on a wall also rejected the
// shopfront GLAZING, which is inset by the wall's thickness, and any recessed
// wall panel with it. No margin separates those, because what distinguishes a
// window from a shelf is not where it is but how big it is — so the margin is
// generous now and --shell-min throws out the furniture it lets back in.
// 20260727e — shell sorted by ORIENTATION rather than size or thinness, both of
// which had the furniture on the wrong side: the chiller run is longer than the
// door AND thinner than the pillars, so no single threshold works. Which way a
// surface faces does work — vertical panels are walls, glazing and doors; a
// horizontal one is the roof, the floor, or (in mid-air) the ceiling grid and
// light battens nobody can see from the street. 34,173 tris -> 446, and the
// rafters, lights and 125k of display cases are out.
// 20260728a — the shell gets its ROOF back. One thickness rule was covering both
// horizontal cases, and it was written for the floor (a genuine 0.00-thick
// sheet); the parapet band above the shopfront is 2.86 deep, seven times that
// limit, so the building came out with no lid and no fascia. Roofs are allowed
// to be real constructions now. That re-admits the ceiling grid, which is the
// right call at this size — 2,640 triangles in a 3,000-triangle shell, and
// visible THROUGH THE SHOPFRONT GLASS. Excluding it only made sense when the
// shell was 34,000 triangles and it was hidden behind an opaque wall.
export const ASSET_VERSION = '20260728a';

// WHERE THE HEAVY CONTENT LIVES. Empty = same origin (files sit in public/ and
// ride along in dist), which is where we started and still the default.
//
// The case for moving it off: `dist` is ~380MB, and only ~24MB of that is the
// application. The rest is models, splat data, videos and barks — content that
// changes on a different rhythm from the code and is re-uploaded in full on
// every deploy. The avatar models alone are 109MB and grow with every artist,
// so this gets worse on its own.
//
// Pointing this at a bucket or CDN makes a deploy the app again, and — the part
// that matters for the workflow — means DROPPING IN A NEW MODEL NO LONGER NEEDS
// A DEPLOY AT ALL. Set VITE_ASSET_BASE (no trailing slash) to switch; the bucket
// must send CORS headers, because GLTFLoader and KTX2 are CORS-gated the same
// way WebGL textures are.
export const ASSET_BASE = (import.meta.env?.VITE_ASSET_BASE || '').replace(/\/$/, '');
export const assetV = (path) => `${ASSET_BASE}${path}${path.includes('?') ? '&' : '?'}v=${ASSET_VERSION}`;

// ── Ultrawide guard ─────────────────────────────────────────────────────────
// On a 32:9 monitor a viewport-anchored overlay sits in the far periphery — the
// nav is by your left shoulder and the map is by your right, and you physically
// turn your head to find them. So the chrome lives inside a column no wider than
// ~one screen's worth, centred, while the 3D world keeps the whole display.
//
// Expressed against VIEWPORT HEIGHT rather than a fixed pixel width, so it is
// self-scaling: 180vh is just past 16:9, which means this is a no-op on every
// ordinary display (1080p, 1440p, laptops) and only bites once a viewport is
// genuinely wider than tall-times-1.8. The 1280px floor protects short windows.
export const HUD_MAX = 'min(100vw, max(1280px, 180vh))';

// The gutter between the viewport edge and that column, for overlays that are
// `position: fixed` in their own right (the phone, the tuning panels) and so
// can't just live inside the HUD's box.
export const edgeInset = (base = 16, side = 'right') =>
  `calc((100vw - ${HUD_MAX}) / 2 + max(${base}px, env(safe-area-inset-${side})))`;

export const API_BASE = resolveBase(import.meta.env.VITE_API_BASE).replace(/\/$/, '');
export const SOCKET_BASE = resolveBase(import.meta.env.VITE_SOCKET_BASE || import.meta.env.VITE_API_BASE).replace(/\/$/, '');
// Auth for billed features (live video chat). Sources, in order:
// VITE_AUTH_TOKEN (.env) → ?token=<jwt> in the URL (saved locally, so it only
// has to be pasted once) → previously saved token.
const qsToken = typeof window !== 'undefined' ? new URLSearchParams(window.location.search).get('token') : null;
if (qsToken) {
  try { localStorage.setItem('world:authToken', qsToken); } catch { /* private mode */ }
  // Scrub the token from the URL immediately (keep the other params) so it
  // doesn't linger in the address bar / browser history after the handoff.
  try {
    const u = new URL(window.location.href);
    u.searchParams.delete('token');
    window.history.replaceState(null, '', u.pathname + u.search + u.hash);
  } catch { /* no-op */ }
}
let savedToken = '';
try { savedToken = localStorage.getItem('world:authToken') || ''; } catch { /* private mode */ }
// Tolerate the common paste shapes: surrounding quotes and a copied
// "Bearer " prefix (we add Bearer ourselves on every request).
const cleanToken = (t) => String(t || '').trim().replace(/^["']|["']$/g, '').replace(/^Bearer\s+/i, '');
// ?asguest=1 → simulate a logged-out visitor (ignore any saved token) so the
// owner can test the guest email-gate chat without clearing localStorage.
const ASGUEST = typeof window !== 'undefined' && new URLSearchParams(window.location.search).get('asguest') != null;
/* ⚠ AN EXPIRED TOKEN IS DROPPED HERE, BEFORE ANYTHING LOADS (2026-10-31). Khari:
   "it loads 1-100 twice". A saved token past its `exp` booted the whole world as
   a member, then useStoryAccess found it invalid and RELOADED as a guest — a
   second full boot. The JWT says when it expires, so a dead one is caught at the
   first line instead (the server check stays the backstop for a revoked one).
   Undecodable or no `exp`: kept, and the server decides, as before. */
const expired = (t) => {
  try {
    const p = JSON.parse(atob(String(t).split('.')[1].replace(/-/g, '+').replace(/_/g, '/')));
    return Number.isFinite(Number(p.exp)) && Number(p.exp) * 1000 < Date.now() + 30000;
  } catch { return false; }
};
const rawToken = ASGUEST ? '' : cleanToken(import.meta.env.VITE_AUTH_TOKEN || qsToken || savedToken);
if (rawToken && expired(rawToken) && rawToken === cleanToken(savedToken)) {
  try { localStorage.removeItem('world:authToken'); } catch { /* private mode */ }
  if (typeof console !== 'undefined') console.info('[auth] saved sign-in has expired — starting as a guest');
}
export const AUTH_TOKEN = rawToken && expired(rawToken) ? '' : rawToken;
// No login → GUEST. Guests chat via the email-gated `/api/chat/guest` pipeline
// (text, no auth, 10 free msgs, logged as leads); logged-in users get the full
// voice chat. `world:guestEmail` persists a guest's email so they gate once.
export const IS_GUEST = !AUTH_TOKEN;
export function guestEmail() { try { return localStorage.getItem('world:guestEmail') || ''; } catch { return ''; } }
export function setGuestEmail(email) { try { localStorage.setItem('world:guestEmail', email); } catch { /* private */ } }

// Decode the JWT payload (client-side, unverified — for UI gating only, never
// trust for anything the server must enforce). → { userId, isAdmin }.
// isAdmin: the token's own claim, or the owner account (userId 1) as fallback.
// ?asguest=1 forces non-admin so the gate can be tested from an admin login.
export function authUser() {
  try {
    if (typeof window !== 'undefined' && new URLSearchParams(window.location.search).get('asguest')) {
      return { userId: null, isAdmin: false };
    }
    const payload = (AUTH_TOKEN || '').split('.')[1];
    const p = JSON.parse(atob(payload.replace(/-/g, '+').replace(/_/g, '/')));
    return { userId: p.userId ?? null, isAdmin: p.isAdmin === true || p.userId === 1 };
  } catch { return { userId: null, isAdmin: false }; }
}

// The slice: Hip-Hop Town (HypeTown) — Oakland, CA. The town lives on Oakland local time
// (see src/worldTime.js); golden hour is its hero look, pinnable for demos
// via ?time=golden.
// ── THE COME UP IS HIDDEN ───────────────────────────────────────────────────
// Khari, 2026-10-23: *"We will need to rewrite how this happens. I see this as
// the onboarding, but also the 'setting free' of the avatar. The Come Up would be
// the only time the user gets control of the character before letting them start
// making their own decisions… since we haven't fully fleshed it out, we should
// hide it for now."* The city is now where the artists LIVE (DOCS/WORLD/
// Living_City.md); everyone enters as a visitor. Off means: no character select,
// no Lyra call, no ride, no salon/studio acts, no walk guide, no premiere, no
// guest claim, and the studio's story door is open like any other. Nothing is
// deleted — this one switch brings it all back when the rewrite lands.
export const COME_UP = false;

export const SLICE_DISTRICT = {
  id: 'hiphop-town',
  name: 'HypeTown',
  city: 'Oakland',
  timezone: 'America/Los_Angeles',
  // The station powering this district — its music plays and its HOST is the DJ
  // billboard cameo. Set on the BACKEND .env (WORLD_STATION) and loaded at boot by
  // loadWorldConfig() below; this is just the pre-fetch default.
  stationSlug: 'hiphop',
  // The weather is the city's own, live: the National Weather Service station
  // whose observations drive the rain, clouds and fog (render/weather.js).
  // KOAK = Oakland International Airport. null turns live weather off.
  weatherStation: 'KOAK',
  palette: {
    sky: '#ff9a3c',
    horizon: '#ffd27d',
    fog: '#e8935a',
    sun: '#ffb25e',
    asphalt: '#2b2b30',
    neon: '#ff5e78'
  }
};

// Backend-set concert-hall performer (CONCERT_HALL_1_PERFORMER env → /api/world/config).
// A URL to a transparent-PNG image OR a video, auto-detected by extension (works
// with signed gallery URLs — the extension sits before the '?'). Loaded at boot
// like the station; overrides rooms.js concert-hall.performer. Update the env with
// a fresh signed URL any time — no rebuild. Live per-session override: ?perfimg /
// ?perfvideo. Null until a config with a performer arrives.
export const WORLD_PERFORMER = { img: null, video: null };

// Pull the district config from the API (GET /api/world/config) and apply it
// BEFORE the app renders — so changing the station/host or district name only
// needs the BACKEND .env (WORLD_STATION / WORLD_NAME / WORLD_TZ) + an API
// restart, never a World rebuild. audioEngine + worldTime read SLICE_DISTRICT at
// call time, so mutating it here (pre-render) is enough. Falls back to the
// defaults above if the fetch fails/times out.
export async function loadWorldConfig() {
  try {
    const ctrl = new AbortController();
    const t = setTimeout(() => ctrl.abort(), 2500);
    const res = await fetch(`${API_BASE}/api/world/config`, { cache: 'no-store', signal: ctrl.signal });
    clearTimeout(t);
    if (!res.ok) return;
    const cfg = await res.json();
    if (cfg.stationSlug) SLICE_DISTRICT.stationSlug = String(cfg.stationSlug);
    if (cfg.name) SLICE_DISTRICT.name = cfg.name;
    if (cfg.timezone) SLICE_DISTRICT.timezone = cfg.timezone;
    // Who stands at the headquarters' front desk (the platform's assistant).
    if (Number(cfg.assistantAvatarId) > 0) SLICE_DISTRICT.assistantAvatarId = Number(cfg.assistantAvatarId);
    if (cfg.performer) {
      WORLD_PERFORMER.img = cfg.performer.img || null;
      WORLD_PERFORMER.video = cfg.performer.video || null;
    }
  } catch { /* keep build-time defaults */ }
}
// ── THE HUD, CLEANED UP (Khari, 2026-10-23, on a phone: "please help me cleanup
// the HUD. We can hide the district, VR, and map buttons… You can also hide the
// fullscreen button. We will save that mode for the TV app later on." — and
// then: "leave the map in. but we will redesign it"). Switches, not deletions:
// the code behind each stays, for the TV app and for later.
export const HUD_FULLSCREEN = false;   // ⛶ (and iOS's Add-to-Home-Screen prompt)
export const HUD_VR = false;           // VR mode (gyro look) and its motion-permission note
export const HUD_UP_FROM_STREET = false; // "← District" while on the street (Back inside a venue stays)

// The way back to the website (Khari, 2026-10-23: "I have placed a link in the
// website pointing to the 3D world. We need a link in the UI to return to
// hypetunes"). The public site is always www.hypetunes.com; VITE_HOME_URL
// points a local build at a local web app instead.
export const HOME_URL = import.meta.env.VITE_HOME_URL || 'https://www.hypetunes.com/';
